<template>
  <div id="party-edit" class="party-edit" :class="{ desktop: $isDesktop }">
    <pu-modal ref="puModal" :use-portal="true" modal-hash="party-edit">
      <template #header>
        <div />
      </template>
      <template #modal-trigger>
        <div>
          <template v-if="showPartyDetails">
            <template
              v-if="
                partySizeChosen &&
                personsIncluded === pitchtypeCapacity &&
                pitchtypeCapacity !== 0 &&
                chargetypeState !== 'max_capacity' &&
                chargetypeState !== 'min_nights' &&
                chargetypeState !== 'adults_only' &&
                chargetypeState !== 'unavailable'
              "
            >
              <translate
                v-if="hasChildren"
                class="party-details"
                :tt="{ adults: party.adults, childrenCount }"
              >
                {adults} adults, {childrenCount} children
              </translate>
              <translate
                v-else
                class="party-details"
                :tt="{ adults: party.adults }"
              >
                {adults} adults
              </translate>
            </template>
          </template>
          <button class="change-btn" data-test="add-more-people-button">
            <template v-if="!partySizeChosen && pitchtypeCapacity === 0">
              <translate>Add/remove people (up to unlimited)</translate>
            </template>
            <template
              v-else-if="
                !partySizeChosen &&
                partySize < pitchtypeCapacity &&
                chargetypeState !== 'adults_only' &&
                !maxDogs &&
                !dogsExceed
              "
            >
              <translate :tt="{ pitchtypeCapacity }">
                Add/remove people (up to {pitchtypeCapacity})
              </translate>
            </template>
            <template v-else-if="maxDogs === null || maxDogs > 0 || dogsExceed">
              <translate
                v-if="maxDogs === null"
                :tt="{ personCapacity: capacityDisplay }"
              >
                Change (max. {personCapacity} persons, unlimited dogs)
              </translate>
              <translate
                v-else
                :tt="{ personCapacity: capacityDisplay, dogCapacity: maxDogs }"
              >
                Change (max. {personCapacity} persons, {dogCapacity} dogs)
              </translate>
            </template>
            <template v-else-if="partySizeChosen || pitchtypeCapacity === 1">
              <translate :tt="{ capacity: capacityDisplay }">
                Change (max. {capacity})
              </translate>
            </template>
          </button>
        </div>
      </template>
      <guests-form
        :guests="party"
        :pitchtype-capacity="pitchtypeCapacity"
        class="party-picker"
        @form-closed="closeModal"
        @partyChanged="partyChanged"
      />
    </pu-modal>
  </div>
</template>

<script lang="ts">
import GuestsForm from '~/components/forms/guests-form/GuestsForm.vue'
import PuModal from '~/components/ui/PuModal.vue'
import {
  Component,
  Prop,
  Ref,
  Vue,
  toNative,
} from '~/utility/pu-class-decorator'
import type { Party } from '../../../types'
import type {
  ChargeType,
  CostsBreakdownPitchtypeDetails,
} from '../../../../campsite-details/types'
import type { PartyValidationResult } from '../../../../../utility/campsite/party-validation'

@Component({
  components: { GuestsForm },
})
class PartyEdit extends Vue {
  @Prop({ required: true })
  pitchtype: CostsBreakdownPitchtypeDetails

  @Prop({ required: true })
  party: Party

  @Prop()
  pitchtypeCapacity: number

  @Prop()
  chargetypeState: string

  @Prop()
  capacityDisplay: string

  @Prop()
  personsIncluded: number

  @Prop()
  partyValidation: PartyValidationResult

  @Prop({ default: true })
  showPartyDetails: boolean

  @Ref()
  private readonly puModal: InstanceType<typeof PuModal>

  get partySize() {
    return this.party.adults + this.party.childAges.length
  }

  get childrenCount() {
    return this.party ? this.party.childAges.length : 0
  }

  get hasChildren(): boolean {
    return this.childrenCount > 0
  }

  get partySizeChosen() {
    return !!this.$route.query.adults || !!this.$route.query.children
  }

  get isOvercapacityMessage() {
    return this.chargetype?.isOvercapacityMessage
  }

  get maxDogs() {
    // Check chargetype first, then partyValidation, preserving null as unlimited
    const chargetypeMax = this.chargetype?.maxDogs
    const validationMax = this.partyValidation?.capacityDetails?.maxDogs

    if (chargetypeMax !== undefined) {
      return chargetypeMax === null ? null : chargetypeMax || 0
    }

    if (validationMax !== undefined) {
      return validationMax === null ? null : validationMax || 0
    }

    return 0 // Default when both are undefined
  }

  get dogsExceed() {
    return (
      this.chargetype?.dogsExceed ||
      (this.partyValidation?.capacityDetails &&
        this.partyValidation.capacityDetails.maxDogs !== null &&
        this.partyValidation.capacityDetails.totalDogs >
          this.partyValidation.capacityDetails.maxDogs) ||
      0
    )
  }

  get chargetype(): ChargeType {
    return this.pitchtype?.chargetype
  }

  closeModal() {
    this.puModal.closeModal()
  }

  partyChanged(party: Party) {
    // we need to bubble here to break out of the modal
    this.$bubble('partyChanged', party)
  }
}

export default toNative(PartyEdit)
</script>

<style scoped>
.party-edit {
  display: flex;
  align-items: center;
  justify-content: flex-start;
}

.party-details {
  font-size: var(--body);
}

.party-details:after {
  content: ')';
}

.party-details:before {
  content: '(';
}

.change-btn {
  border: none;
  box-shadow: none;
  margin: 0;
  color: var(--colour-dark-blue);
  text-decoration: underline;
  padding: 0 0 0.5em 0;
}

.change-btn :deep(span) {
  font-size: var(--body);
}

.party-picker {
  padding: 1em;
}

.party-edit.desktop .change-btn :deep(span),
.party-edit.desktop .party-details {
  font-size: inherit;
}
</style>
